import * as React from 'react';
import * as Styled from './styled';
import { List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import { Info, LibraryBooks, SettingsApplications, SignalWifiOff, Timelapse } from '@material-ui/icons';
import CustomDictionaryContainer from '../CustomDictionary/CustomDictionaryContainer';
import AboutEpochContainer from '../AboutEpoch/AboutEpochContainer';
import { Redirect, Route } from 'react-router';
import rootStore from 'store/root.store';
import TimeCastSettingsContainer from '../TimeCastSettings/TimeCastSettingsContainer';
import { FeaturesConsumer } from 'common/FeaturesProvider';
import { Features } from '../../api/types/types';
import OfflineEntries from 'components/OfflineEntries/OfflineEntries';
import { Platform } from '../../util/Platform';
import GeneralSettings from '../GeneralSettings/GeneralSettings';
import { withTranslation } from 'react-i18next';
import { handleEscapeKey } from 'util/hotKeys';

interface Props {
    location: {
        pathname: string;
    };
    match: {
        url: string;
        path: string;
        params: {
            id: string;
        }
    };
    // tslint:disable-next-line:no-any
    t: any;
}

enum Tab {
    GENERAL,
    CUSTOM_DICTIONARY,
    TIMECAST,
    ABOUT,
    OFFLINE,
    NONE
}

interface State {
    selectedTab: Tab;
}

class Settings extends React.Component<Props, State> {
    rightPanelRef = React.createRef<HTMLElement>();
    formFieldRef = React.createRef<HTMLElement>();
    fabRef = React.createRef<HTMLElement>();
    firstTabRef = React.createRef<HTMLElement>();
    lastTabRef = React.createRef<HTMLElement>();

    state: State = {
        selectedTab: Tab.GENERAL
    };

    constructor(props: Props) {
        super(props);
        this.setState({
            selectedTab: props.location.pathname.startsWith('Offline') ? Tab.OFFLINE : Tab.NONE
        });
    }

    componentWillMount = () => {
        let tabToBeSelected;
        switch (this.props.location.pathname) {
            case '/settings/about':
                tabToBeSelected = Tab.ABOUT;
                break;
            case '/settings/timecast':
                tabToBeSelected = Tab.TIMECAST;
                break;
            case '/settings/customDictionary':
                tabToBeSelected = Tab.CUSTOM_DICTIONARY;
                break;
            case '/settings/offlineEntries':
                tabToBeSelected = Tab.OFFLINE;
                break;
            case '/settings/general':
                tabToBeSelected = Tab.GENERAL;
                break;
            default:
                tabToBeSelected = Tab.GENERAL;
        }

        this.setState({ selectedTab: tabToBeSelected });
    }

    selectTab = async (el: HTMLElement, tab: Tab) => {
        if (await rootStore.canIChangeScope()) {
            if (tab !== Tab.ABOUT) {
                rootStore.appStore.setPreviousActiveElement();
            }
            if (this.formFieldRef.current) {
                this.formFieldRef.current.focus();
            }
            if (this.state.selectedTab === tab) {
                return;
            }
            this.setState({ selectedTab: tab });

            switch (tab) {
                case Tab.ABOUT:
                    rootStore.routerStore.push('/settings/about');
                    break;
                case Tab.TIMECAST:
                    rootStore.routerStore.push('/settings/timecast');
                    break;
                case Tab.CUSTOM_DICTIONARY:
                    rootStore.routerStore.push('/settings/customDictionary');
                    break;
                case Tab.OFFLINE:
                    rootStore.routerStore.push(`/settings/offlineEntries`);
                    break;
                case Tab.GENERAL:
                    rootStore.routerStore.push('/settings/general');
                    break;
                default:
                    rootStore.routerStore.push('/settings/general');
                    break;
            }
        }
    }
    render() {
        const { match, t } = this.props;
        const { offlineEntryCount } = rootStore.appStore!;

        return (
            <Styled.MainWrapper>
                <Styled.Header>
                    <Styled.Title variant="h6">
                        {t('header.title')}
                    </Styled.Title>
                </Styled.Header>
                <Styled.WrapperContent>
                    <Styled.ListContainer>
                        <List>
                            {/* FIXME: Selected list item should be dynamic when more settings pitch in*/}
                            <Styled.ListItemContainer>
                                <ListItem
                                    buttonRef={this.firstTabRef}
                                    data-auto-focus={true}
                                    button={true}
                                    selected={this.state.selectedTab === Tab.GENERAL}
                                    onClick={(e) => this.selectTab(e.currentTarget, Tab.GENERAL)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Tab' && e.shiftKey) {
                                            e.preventDefault();
                                            this.lastTabRef.current!.focus()
                                        }
                                    }}
                                >
                                    <ListItemIcon>
                                        <SettingsApplications/>
                                    </ListItemIcon>
                                    <ListItemText primary={t('nav_list.item.general')}/>
                                </ListItem>
                            </Styled.ListItemContainer>
                            <Styled.ListItemContainer>
                                <ListItem
                                    id="dictonary_tab"
                                    button={true}
                                    selected={this.state.selectedTab === Tab.CUSTOM_DICTIONARY}
                                    onClick={(e) => this.selectTab(e.currentTarget, Tab.CUSTOM_DICTIONARY)}
                                >
                                    <ListItemIcon>
                                        <LibraryBooks/>
                                    </ListItemIcon>
                                    <ListItemText primary={t('nav_list.item.dictionary')}/>
                                </ListItem>
                            </Styled.ListItemContainer>
                            <FeaturesConsumer>
                                {(features: Features) => features.EpochConfigTimeCastEnabled &&
                                    <Styled.ListItemContainer>
                                        <ListItem
                                            id="timecast_tab"
                                            button={true}
                                            selected={this.state.selectedTab === Tab.TIMECAST}
                                            onClick={(e) => this.selectTab(e.currentTarget, Tab.TIMECAST)}
                                        >
                                            <ListItemIcon>
                                                <Timelapse/>
                                            </ListItemIcon>
                                            <ListItemText primary={t('nav_list.item.timecast')}/>
                                        </ListItem>
                                    </Styled.ListItemContainer>
                                }
                            </FeaturesConsumer>
                            {Platform.isElectron() && offlineEntryCount > 0 &&
                            <Styled.ListItemContainer>
                                <ListItem
                                    id="offline_entries"
                                    button={true}
                                    selected={this.state.selectedTab === Tab.OFFLINE}
                                    onClick={(e) => this.selectTab(e.currentTarget, Tab.OFFLINE)}
                                >
                                    <ListItemIcon>
                                        <SignalWifiOff/>
                                    </ListItemIcon>
                                    <ListItemText primary={t('nav_list.item.desktop.offline_entries')}/>
                                </ListItem>
                            </Styled.ListItemContainer>
                            }
                            <Styled.ListItemContainer>
                                <ListItem
                                    buttonRef={this.lastTabRef}
                                    button={true}
                                    selected={this.state.selectedTab === Tab.ABOUT}
                                    onClick={(e) => this.selectTab(e.currentTarget, Tab.ABOUT)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Tab' && !e.shiftKey) {
                                            e.preventDefault();
                                            this.firstTabRef.current!.focus();
                                        }
                                    }}
                                >
                                    <ListItemIcon>
                                        <Info/>
                                    </ListItemIcon>
                                    <ListItemText primary={t('nav_list.item.about')}/>
                                </ListItem>
                            </Styled.ListItemContainer>
                        </List>
                    </Styled.ListContainer>
                    <Styled.PageContainer innerRef={this.rightPanelRef} onKeyDown={e => handleEscapeKey(e, this.rightPanelRef.current, this.fabRef.current)}>
                        <Route
                            exact={false}
                            path="/settings/userDictionary"
                            render={() => (
                                <Redirect to={`${match.url}/customDictionary`} />
                            )}
                        />
                        <Route path={`${match.path}/customDictionary`} render={() => <CustomDictionaryContainer formFieldRef={this.formFieldRef} />}/>
                        <Route path={`${match.path}/offlineEntries`} render={() => <OfflineEntries formFieldRef={this.formFieldRef} />}/>
                        <Route path={`${match.path}/about`} component={AboutEpochContainer}/>
                        <Route path={`${match.path}/general`} render={() => <GeneralSettings formFieldRef={this.formFieldRef} />}/>
                        <FeaturesConsumer>
                            {(features: Features) => 
                                features.EpochConfigTimeCastEnabled &&
                                <Route
                                    path={`${match.path}/timecast`}
                                    render={() => <TimeCastSettingsContainer formFieldRef={this.formFieldRef} fabRef={this.fabRef}/>}
                                />
                            }
                        </FeaturesConsumer>
                        {/* <CustomDictionaryContainer/> */}
                    </Styled.PageContainer>
                </Styled.WrapperContent>
            </Styled.MainWrapper>
        )
    }
}

export default withTranslation(['settings'])(Settings);